/**
 * Catálogo de widgets do Dashboard Financeiro (FIN-LAYOUT).
 *
 * Cada widget é uma entrada `{ id, titulo, sub, aba, span, Comp, exportar }`:
 *  - `Comp({ dados, janela, chartCfg, navigate })` renderiza o corpo (.cc__body
 *    ou .block__body — o card/chrome é montado pelo index).
 *  - `exportar(dados)` → `{ nome, colunas, linhas }` para o CSV por widget.
 *
 * Todos os widgets são backed por dados REAIS de
 * GET /api/financeiro/dashboard-executivo (sem widgets fabricados).
 */
import React from "react";
import {
  Area, AreaChart, Bar, CartesianGrid, Cell, ComposedChart, Line,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { FiArrowDownRight, FiArrowUpRight } from "react-icons/fi";

import { formatarReais } from "../../../services/financeiro";
import { EmptyMsg } from "../../Orcamentos/styles";

export function num(v) { return Number(v || 0); }
export function fmtK(v) {
  const n = Number(v || 0); const abs = Math.abs(n); const s = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${s}R$ ${(abs / 1_000_000).toFixed(2).replace(".", ",").replace(/,?0+$/, "")}M`;
  if (abs >= 1_000) return `${s}R$ ${Math.round(abs / 1000)}k`;
  return `${s}R$ ${abs.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`;
}

export const PALETAS = {
  flux: { nome: "Padrão · Flux", cores: ["var(--chart-2)", "var(--chart-5)", "var(--chart-1)"] },
  vibrant: { nome: "Vibrant", cores: ["var(--chart-3)", "var(--chart-4)", "var(--chart-6)"] },
  mono: { nome: "Mono slate", cores: ["var(--ink-2)", "var(--ink-3)", "var(--ink-4)"] },
  violet: { nome: "Violet scale", cores: ["var(--accent)", "var(--chart-1)", "var(--chart-7)"] },
  semaforo: { nome: "Semáforo", cores: ["var(--success-solid)", "var(--danger-solid)", "var(--warning-solid)"] },
};
const CORES = ["var(--chart-3)", "var(--chart-2)", "var(--chart-5)", "var(--chart-4)", "var(--chart-1)"];

// ---------------------------------------------------------------- componentes

function EntradasSaidas({ dados, chartCfg }) {
  const serie = dados?.operacional?.serie_diaria || [];
  const data = serie.map((d) => ({ label: d.label, entradas: num(d.entradas), saidas: num(d.saidas), acumulado: num(d.saldo_acumulado) }));
  if (data.length === 0) return <EmptyMsg>Sem movimentação no período.</EmptyMsg>;
  const cfg = chartCfg || { tipo: "bar", paleta: "flux", grid: true };
  const [cE, cS, cA] = PALETAS[cfg.paleta]?.cores || PALETAS.flux.cores;
  const tipo = cfg.tipo || "bar";
  return (
    <>
      <div className="legend-row" style={{ marginBottom: 8 }}>
        <span className="legend-item"><span className="dot" style={{ background: cE }} /> Entradas</span>
        <span className="legend-item"><span className="dot" style={{ background: cS }} /> Saídas</span>
        <span className="legend-item"><span className="dot" style={{ background: cA }} /> Saldo acumulado</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        {tipo === "area" ? (
          <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            {cfg.grid && <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />}
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-3)" }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={34} />
            <Tooltip formatter={(v) => formatarReais(v)} />
            <Area type="monotone" dataKey="entradas" stroke={cE} fill={cE} fillOpacity={0.25} name="Entradas" />
            <Area type="monotone" dataKey="saidas" stroke={cS} fill={cS} fillOpacity={0.25} name="Saídas" />
            <Line type="monotone" dataKey="acumulado" stroke={cA} strokeWidth={2} dot={false} name="Saldo acumulado" />
          </AreaChart>
        ) : (
          <ComposedChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            {cfg.grid && <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />}
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-3)" }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={34} />
            <Tooltip formatter={(v) => formatarReais(v)} />
            {tipo === "bar" ? (
              <>
                <Bar dataKey="entradas" fill={cE} radius={[3, 3, 0, 0]} name="Entradas" />
                <Bar dataKey="saidas" fill={cS} radius={[3, 3, 0, 0]} name="Saídas" />
              </>
            ) : (
              <>
                <Line type="monotone" dataKey="entradas" stroke={cE} strokeWidth={2} dot={false} name="Entradas" />
                <Line type="monotone" dataKey="saidas" stroke={cS} strokeWidth={2} dot={false} name="Saídas" />
              </>
            )}
            <Line type="monotone" dataKey="acumulado" stroke={cA} strokeWidth={2} dot={false} name="Saldo acumulado" />
          </ComposedChart>
        )}
      </ResponsiveContainer>
    </>
  );
}

function Donut({ itens, rotuloCentro }) {
  const data = (itens || []).map((c, i) => ({ name: c.categoria, value: num(c.total), pct: c.pct, fill: CORES[i % CORES.length] }));
  const total = data.reduce((a, d) => a + d.value, 0);
  if (data.length === 0) return <EmptyMsg>Sem dados no período.</EmptyMsg>;
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
      <div style={{ position: "relative", width: 150, height: 150, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={70} paddingAngle={2}>
              {data.map((e) => <Cell key={e.name} fill={e.fill} />)}
            </Pie>
            <Tooltip formatter={(v) => formatarReais(v)} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center", pointerEvents: "none" }}>
          <div><div className="mono" style={{ fontSize: 14, fontWeight: 700 }}>{fmtK(total)}</div><div className="tiny muted">{rotuloCentro}</div></div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {data.map((d) => (
          <div key={d.name} className="flex between" style={{ fontSize: 12.5, padding: "3px 0" }}>
            <span className="ell"><span className="dot" style={{ background: d.fill, display: "inline-block", width: 9, height: 9, borderRadius: 2, marginRight: 6 }} />{d.name}</span>
            <strong className="mono">{d.pct != null ? `${d.pct}%` : fmtK(d.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function Projecao({ dados }) {
  const buckets = dados?.fluxo_caixa?.buckets || [];
  const data = buckets.map((b) => ({ label: (b.label || "").replace(/Sem \d+ /, "S"), saldo: num(b.saldo_acumulado) }));
  if (data.length === 0) return <EmptyMsg>Sem projeção disponível.</EmptyMsg>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-3)" }} />
        <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={34} />
        <Tooltip formatter={(v) => formatarReais(v)} />
        <Area type="monotone" dataKey="saldo" stroke="var(--accent)" strokeWidth={2} fill="url(#projGrad)" name="Saldo projetado" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function ProximasMov({ dados }) {
  const itens = dados?.operacional?.proximas_movimentacoes || [];
  if (itens.length === 0) return <EmptyMsg>Nenhuma movimentação prevista.</EmptyMsg>;
  return (
    <div style={{ margin: "0 -18px -18px" }}>
      {itens.map((m) => {
        const entrada = m.tipo === "ENTRADA";
        return (
          <div className="money-row" key={m.id}>
            <div className={"ic" + (entrada ? "" : " expense")}>{entrada ? <FiArrowUpRight size={14} /> : <FiArrowDownRight size={14} />}</div>
            <div className="body">
              <div className="nm">{m.descricao}</div>
              <div className="sub">{new Date(`${m.data}T00:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}{m.categoria ? ` · ${m.categoria}` : ""}</div>
            </div>
            <div className={"val" + (entrada ? "" : " expense")}>{entrada ? "+" : "−"}{formatarReais(m.valor)}</div>
          </div>
        );
      })}
    </div>
  );
}

function ContasSaldos({ dados }) {
  const contas = dados?.contas_bancarias?.contas || [];
  if (contas.length === 0) return <EmptyMsg>Nenhuma conta cadastrada.</EmptyMsg>;
  return (
    <div className="stack" style={{ gap: 8 }}>
      {contas.map((c, i) => (
        <div key={c.id || i} className="flex between" style={{ fontSize: 13, padding: "6px 0", borderBottom: i < contas.length - 1 ? "1px solid var(--divider)" : "none" }}>
          <span className="ell">{c.nome}{c.banco_nome ? <span className="tiny muted"> · {c.banco_nome}</span> : ""}</span>
          <strong className="mono" style={{ color: num(c.saldo_atual) < 0 ? "var(--danger-text)" : "var(--ink)" }}>{fmtK(c.saldo_atual)}</strong>
        </div>
      ))}
    </div>
  );
}

function StatusFiscalCobranca({ dados }) {
  const s = dados?.status_fiscal_cobranca || {};
  const card = (lbl, val, sub, tone) => (
    <div className="block" style={{ borderRadius: "var(--radius-lg)" }}>
      <div className="block__body" style={{ padding: "12px 14px" }}>
        <div className="lbl" style={{ fontFamily: "var(--font-mono)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink-3)" }}>{lbl}</div>
        <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 4, color: tone }}>{val}</div>
        <div className="tiny muted" style={{ marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
  return (
    <div className="card-grid-2" style={{ gap: 10 }}>
      {card("NF-e no mês", s.nf_emitidas_mes ?? 0, fmtK(s.nf_valor_total_mes), undefined)}
      {card("Boletos em aberto", s.boletos_pendentes_count ?? 0, fmtK(s.boletos_pendentes_valor), undefined)}
      {card("Boletos vencidos", s.boletos_vencidos_count ?? 0, "requerem atenção", num(s.boletos_vencidos_count) > 0 ? "var(--danger-text)" : undefined)}
    </div>
  );
}

function ResultadoMensal({ dados }) {
  const serie = dados?.diretor?.resultado_mensal || [];
  const data = serie.map((d) => ({ mes: d.mes, v: num(d.resultado) }));
  if (data.length === 0) return <EmptyMsg>Sem histórico.</EmptyMsg>;
  const max = Math.max(...data.map((d) => Math.abs(d.v)), 1);
  return (
    <>
      <div className="diverge-chart" role="img" aria-label="Resultado mês a mês">
        <div className="dc-pos">{data.map((d, i) => <div key={i} className="b" style={{ height: d.v > 0 ? `${(d.v / max) * 96}px` : 0 }} />)}</div>
        <div className="dc-zero" />
        <div className="dc-neg">{data.map((d, i) => <div key={i} className="b" style={{ height: d.v < 0 ? `${(Math.abs(d.v) / max) * 96}px` : 0 }} />)}</div>
        <div className="dc-labels">{data.map((d, i) => <span key={i}>{(d.mes || "").slice(0, 3)}</span>)}</div>
      </div>
      <div className="legend-row" style={{ marginTop: 12 }}>
        <span className="legend-item"><span className="dot" style={{ background: "var(--chart-1)" }} /> Positivo</span>
        <span className="legend-item"><span className="dot" style={{ background: "var(--danger-solid)" }} /> Negativo</span>
        <span className="legend-item" style={{ marginLeft: "auto", color: "var(--ink-3)" }}>atual: {fmtK(data[data.length - 1]?.v)}</span>
      </div>
    </>
  );
}

function TopObras({ dados }) {
  const obras = dados?.diretor?.top_obras || [];
  if (obras.length === 0) return <EmptyMsg>Sem obras entregues no mês.</EmptyMsg>;
  return (
    <div style={{ margin: "0 -18px -18px" }}>
      {obras.map((o, i) => (
        <div className="prof-row" key={o.pedido + i}>
          <span className={"pos" + (i < 3 ? " top" : "")}>{i + 1}</span>
          <div className="nm"><strong>{o.pedido}</strong><div className="sub">{o.cliente}</div></div>
          <span className="num">{fmtK(o.receita)}</span>
          <span className={"margin-pill " + (num(o.margem_pct) >= 30 ? "high" : num(o.margem_pct) >= 10 ? "mid" : "low")}>
            {num(o.margem_pct).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
          </span>
        </div>
      ))}
    </div>
  );
}

function DespesasDre({ dados }) {
  const lista = dados?.diretor?.despesas_por_classificacao || [];
  if (lista.length === 0) return <EmptyMsg>Sem despesas classificadas no mês.</EmptyMsg>;
  const total = lista.reduce((a, c) => a + Math.abs(num(c.total)), 0) || 1;
  return (
    <div className="stack" style={{ gap: 12 }}>
      {lista.map((c, i) => {
        const pct = Math.round((Math.abs(num(c.total)) / total) * 100);
        return (
          <div key={i}>
            <div className="flex between" style={{ fontSize: 13, marginBottom: 5 }}>
              <span>{c.classificacao}</span>
              <span className="mono" style={{ fontWeight: 600 }}>{fmtK(Math.abs(num(c.total)))} · {pct}%</span>
            </div>
            <div className="comp-bar" style={{ height: 8, borderRadius: 999, background: "var(--surface-2)", overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${pct}%`, background: CORES[i % CORES.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FunilVendas({ dados }) {
  const funil = dados?.funil_vendas || [];
  if (funil.length === 0) return <EmptyMsg>Sem pedidos no funil.</EmptyMsg>;
  const max = Math.max(...funil.map((f) => num(f.quantidade)), 1);
  return (
    <div className="stack" style={{ gap: 8 }}>
      {funil.map((f, i) => {
        const pct = Math.round((num(f.quantidade) / max) * 100);
        return (
          <div key={i}>
            <div className="flex between" style={{ fontSize: 12.5, marginBottom: 4 }}>
              <span>{f.situacao_display || f.situacao}</span>
              <span className="mono" style={{ fontWeight: 600 }}>{f.quantidade}</span>
            </div>
            <div className="comp-bar" style={{ height: 10, borderRadius: 999, background: "var(--surface-2)", overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${pct}%`, background: CORES[i % CORES.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// M-FIN-2 / M-CRM-8: este bloco se chamava "Performance de vendedores" e
// ranqueava PARCEIROS INDICADORES (arquiteto, decorador) pelo RT — quem
// indica a obra, nao quem vende. Ainda por cima renderizava sem nome
// nenhum, porque o payload publicava `vendedor` e a tela lia `nome`.
function PerformanceParceiros({ dados }) {
  const parceiros = dados?.performance_parceiros || dados?.performance_vendedores || [];
  if (parceiros.length === 0) return <EmptyMsg>Sem comissões de parceiro no período.</EmptyMsg>;
  return (
    <div style={{ margin: "0 -18px -18px" }}>
      {parceiros.map((p, i) => (
        <div className="prof-row" key={(p.nome || p.vendedor || "") + i}>
          <span className={"pos" + (i < 3 ? " top" : "")}>{i + 1}</span>
          <div className="nm">
            <strong>{p.nome || p.vendedor || "—"}</strong>
            {p.comissao_a_pagar != null && num(p.comissao_a_pagar) > 0 ? (
              <div className="sub">{fmtK(p.comissao_a_pagar)} a pagar</div>
            ) : null}
          </div>
          <span className="num">{fmtK(p.comissao_total ?? p.total)}</span>
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- catálogo

function expCategorias(itens) {
  return { colunas: ["Categoria", "Total", "%"], linhas: (itens || []).map((c) => [c.categoria, num(c.total).toFixed(2), c.pct ?? ""]) };
}

export const CATALOGO = [
  // ----- operacional -----
  { id: "entradas_saidas", aba: "operacional", titulo: "Entradas × Saídas", sub: "série diária", span: 7, chart: true, Comp: EntradasSaidas,
    exportar: (d) => ({ colunas: ["Dia", "Entradas", "Saídas", "Saldo acumulado"], linhas: (d?.operacional?.serie_diaria || []).map((x) => [x.label, num(x.entradas).toFixed(2), num(x.saidas).toFixed(2), num(x.saldo_acumulado).toFixed(2)]) }) },
  { id: "categorias_saidas", aba: "operacional", titulo: "Saídas por categoria", sub: "no período", span: 5,
    Comp: ({ dados }) => <Donut itens={dados?.top_despesas} rotuloCentro="SAÍDAS" />, exportar: (d) => expCategorias(d?.top_despesas) },
  { id: "categorias_receitas", aba: "operacional", titulo: "Receitas por categoria", sub: "no período", span: 5,
    Comp: ({ dados }) => <Donut itens={dados?.top_receitas} rotuloCentro="RECEITAS" />, exportar: (d) => expCategorias(d?.top_receitas) },
  { id: "projecao_saldo", aba: "operacional", titulo: "Projeção de saldo", sub: "8 semanas · contratos confirmados", span: 7, Comp: Projecao,
    exportar: (d) => ({ colunas: ["Semana", "Saldo acumulado"], linhas: (d?.fluxo_caixa?.buckets || []).map((b) => [b.label, num(b.saldo_acumulado).toFixed(2)]) }) },
  { id: "proximas_mov", aba: "operacional", titulo: "Próximas movimentações", sub: "previstas", span: 5, Comp: ProximasMov,
    exportar: (d) => ({ colunas: ["Data", "Descrição", "Categoria", "Tipo", "Valor"], linhas: (d?.operacional?.proximas_movimentacoes || []).map((m) => [m.data, m.descricao, m.categoria || "", m.tipo, num(m.valor).toFixed(2)]) }) },
  { id: "contas_saldos", aba: "operacional", titulo: "Contas e saldos", sub: "consolidado", span: 5, Comp: ContasSaldos,
    exportar: (d) => ({ colunas: ["Conta", "Banco", "Saldo atual"], linhas: (d?.contas_bancarias?.contas || []).map((c) => [c.nome, c.banco_nome || "", num(c.saldo_atual).toFixed(2)]) }) },
  { id: "status_fiscal_cobranca", aba: "operacional", titulo: "Fiscal e cobrança", sub: "no mês", span: 7, Comp: StatusFiscalCobranca,
    exportar: (d) => { const s = d?.status_fiscal_cobranca || {}; return { colunas: ["Indicador", "Valor"], linhas: [["NF-e emitidas no mês", s.nf_emitidas_mes ?? 0], ["Valor NF-e no mês", num(s.nf_valor_total_mes).toFixed(2)], ["Boletos em aberto", s.boletos_pendentes_count ?? 0], ["Valor em aberto", num(s.boletos_pendentes_valor).toFixed(2)], ["Boletos vencidos", s.boletos_vencidos_count ?? 0]] }; } },
  // ----- diretor -----
  { id: "resultado_mensal", aba: "diretor", titulo: "Resultado mês a mês", sub: "T − OE · últimos 12 meses", span: 7, Comp: ResultadoMensal,
    exportar: (d) => ({ colunas: ["Mês", "Resultado"], linhas: (d?.diretor?.resultado_mensal || []).map((x) => [x.mes, num(x.resultado).toFixed(2)]) }) },
  { id: "top_obras", aba: "diretor", titulo: "Top 5 obras por margem", sub: "no mês", span: 5, bloco: true, Comp: TopObras,
    exportar: (d) => ({ colunas: ["Pedido", "Cliente", "Receita", "Margem %"], linhas: (d?.diretor?.top_obras || []).map((o) => [o.pedido, o.cliente, num(o.receita).toFixed(2), o.margem_pct]) }) },
  { id: "despesas_dre", aba: "diretor", titulo: "Despesas por classificação DRE", sub: "no mês", span: 12, bloco: true, Comp: DespesasDre,
    exportar: (d) => ({ colunas: ["Classificação", "Total"], linhas: (d?.diretor?.despesas_por_classificacao || []).map((c) => [c.classificacao, Math.abs(num(c.total)).toFixed(2)]) }) },
  { id: "funil_vendas", aba: "diretor", titulo: "Funil de vendas", sub: "pedidos por situação", span: 5, bloco: true, Comp: FunilVendas,
    exportar: (d) => ({ colunas: ["Situação", "Quantidade"], linhas: (d?.funil_vendas || []).map((f) => [f.situacao_display || f.situacao, f.quantidade]) }) },
  // `id` preservado: e a chave do layout salvo por usuario. Trocar o id
  // faria o widget sumir do painel de quem ja tinha organizado a tela.
  { id: "performance_vendedores", aba: "diretor", titulo: "Parceiros indicadores", sub: "RT do período", span: 7, bloco: true, Comp: PerformanceParceiros,
    exportar: (d) => ({ colunas: ["Parceiro", "A pagar", "Comissão total"], linhas: (d?.performance_parceiros || d?.performance_vendedores || []).map((p) => [p.nome || p.vendedor || "", num(p.comissao_a_pagar).toFixed(2), num(p.comissao_total ?? p.total).toFixed(2)]) }) },
];

export const CATALOGO_POR_ID = Object.fromEntries(CATALOGO.map((w) => [w.id, w]));

// Ordem default por aba (todos visíveis, na ordem do catálogo).
export function layoutDefault(aba) {
  return CATALOGO.filter((w) => w.aba === aba).map((w) => w.id);
}
